import { Box, Button, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SolarGeometry from '../components/SolarGeometry';

function AnimatedWord({ text, delay, accent }: { text: string; delay: number; accent?: boolean }) {
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-block',
        marginRight: '0.24em',
        opacity: 0,
        animation: 'wordReveal 800ms cubic-bezier(0.2, 0.8, 0.2, 1) forwards',
        animationDelay: `${delay}ms`,
        color: accent ? 'var(--sun)' : 'inherit',
        fontStyle: accent ? 'italic' : 'normal',
      }}
    >
      {text}
    </Box>
  );
}

export default function HomePage() {
  const navigate = useNavigate();
  const { t } = useTranslation('landing');
  const { t: tCommon } = useTranslation('common');

  // Costruzione runtime delle righe del titolo (i18n-friendly).
  // Le parole vengono singolarmente renderizzate con animazione stagger.
  const TITLE_LINES: string[][] = [
    [t('hero.titolo_riga1'), t('hero.titolo_riga2')],
    [t('hero.titolo_riga3')],
  ];
  // Seconda parte del titolo: la 2a parola della prima riga è "accent" (corsivo arancione)
  const SECOND_TITLE_LINE_1: string[] = [
    t('hero.titolo_riga4_pre'),
    t('hero.titolo_riga4_accent'),
  ];
  // L'ultima riga "di fiducia." era splittata in 2 parole nell'originale
  const lastLineText = t('hero.titolo_riga5');
  const SECOND_TITLE_LINE_2: string[] = lastLineText.split(' ');

  let delayCounter = 0;
  const wordDelay = () => {
    const d = 120 + delayCounter * 80;
    delayCounter += 1;
    return d;
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
      }}
    >
      {/* Top bar — sticky */}
      <Box
        component="header"
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: { xs: '16px 24px', md: '20px 56px' },
          background: 'rgba(244, 239, 230, 0.78)',
          backdropFilter: 'blur(14px)',
          WebkitBackdropFilter: 'blur(14px)',
          borderBottom: '1px solid var(--dust)',
          opacity: 0,
          animation: 'fadeIn 600ms ease-out 200ms forwards',
        }}
      >
        {/* Left: logo + beta tag */}
        <Stack direction="row" spacing={2.5} alignItems="center">
          <Typography
            variant="overline"
            sx={{
              fontSize: '0.7rem',
              color: 'var(--ink)',
              display: 'flex',
              alignItems: 'center',
              gap: 1.2,
            }}
          >
            <Box
              component="span"
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                background: 'var(--sun)',
                display: 'inline-block',
              }}
            />
            {tCommon('brand').replace(' ', ' ')}
          </Typography>
          <Box
            component="span"
            sx={{
              display: { xs: 'none', sm: 'inline-block' },
              fontFamily: '"JetBrains Mono Variable", monospace',
              fontSize: '0.62rem',
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              color: 'var(--ink-soft)',
              border: '1px solid var(--dust)',
              padding: '3px 8px',
            }}
          >
            {tCommon('beta_tag')}
          </Box>
        </Stack>

        {/* Right: small CTA */}
        <Button
          variant="text"
          onClick={() => navigate('/iscriviti?ruolo=venditore')}
          sx={{
            background: 'var(--ink)',
            color: 'var(--bg)',
            fontFamily: '"JetBrains Mono Variable", monospace',
            fontSize: '0.72rem',
            fontWeight: 600,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            padding: { xs: '10px 16px', md: '12px 22px' },
            borderRadius: 0,
            transition: 'all 240ms cubic-bezier(0.2, 0, 0.2, 1)',
            '& .arrow': {
              display: 'inline-block',
              marginLeft: '10px',
              transition: 'transform 240ms',
            },
            '&:hover': {
              background: 'var(--sun-deep)',
              '& .arrow': { transform: 'translateX(6px)' },
            },
          }}
        >
          <span>{tCommon('iscriviti')}</span>
          <span className="arrow">&rarr;</span>
        </Button>
      </Box>

      {/* Hero */}
      <Box
        sx={{
          flex: 1,
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '1.3fr 1fr' },
          alignItems: 'center',
          gap: { xs: 4, lg: 8 },
          padding: { xs: '24px 20px 32px', md: '0 56px 48px' },
          maxWidth: '1480px',
          margin: '0 auto',
          width: '100%',
        }}
      >
        {/* Left column: editorial title */}
        <Stack spacing={4} sx={{ position: 'relative' }}>
          {/* Section marker */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              opacity: 0,
              animation: 'fadeIn 600ms ease-out 400ms forwards',
            }}
          >
            <Typography
              variant="overline"
              sx={{ color: 'var(--sun-deep)', fontSize: '0.75rem' }}
            >
              {t('hero.marker_numero')}
            </Typography>
            <Box
              sx={{
                flex: 1,
                height: '1px',
                background: 'var(--dust)',
                maxWidth: 120,
              }}
            />
          </Box>

          {/* Main title */}
          <Typography
            variant="h1"
            component="h1"
            sx={{
              fontSize: { xs: '2.8rem', sm: '3.8rem', md: '5rem', lg: '5.6rem' },
            }}
          >
            <Box component="span" sx={{ display: 'block' }}>
              {TITLE_LINES[0].map((w) => (
                <AnimatedWord key={w} text={w} delay={wordDelay()} />
              ))}
            </Box>
            <Box component="span" sx={{ display: 'block' }}>
              {TITLE_LINES[1].map((w) => (
                <AnimatedWord key={w} text={w} delay={wordDelay()} />
              ))}
            </Box>
            <Box component="span" sx={{ display: 'block', mt: 1 }}>
              {SECOND_TITLE_LINE_1.map((w, i) => (
                <AnimatedWord
                  key={w}
                  text={w}
                  delay={wordDelay()}
                  accent={i === 1}
                />
              ))}
            </Box>
            <Box component="span" sx={{ display: 'block' }}>
              {SECOND_TITLE_LINE_2.map((w) => (
                <AnimatedWord key={w} text={w} delay={wordDelay()} />
              ))}
            </Box>
          </Typography>

          {/* Subtitle */}
          <Box
            sx={{
              maxWidth: 520,
              opacity: 0,
              animation: 'fadeIn 700ms ease-out 1400ms forwards',
            }}
          >
            <Typography
              variant="body1"
              sx={{
                fontSize: { xs: '0.98rem', md: '1.1rem' },
                lineHeight: 1.55,
                color: 'var(--ink-soft)',
              }}
            >
              {t('hero.sottotitolo')}
            </Typography>
          </Box>

          {/* CTA */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={3}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            sx={{
              opacity: 0,
              animation: 'fadeIn 700ms ease-out 1700ms forwards',
            }}
          >
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/iscriviti?ruolo=venditore')}
              sx={{
                background: 'var(--ink)',
                color: 'var(--bg)',
                fontSize: '0.85rem',
                position: 'relative',
                overflow: 'hidden',
                '& .label': {
                  display: 'inline-block',
                  transition: 'transform 240ms cubic-bezier(0.2, 0, 0.2, 1)',
                },
                '& .arrow': {
                  display: 'inline-block',
                  marginLeft: '12px',
                  transition: 'transform 240ms cubic-bezier(0.2, 0, 0.2, 1)',
                },
                '&:hover': {
                  background: 'var(--sun-deep)',
                  '& .arrow': {
                    transform: 'translateX(8px)',
                  },
                },
              }}
            >
              <span className="label">{tCommon('iscriviti_gratis')}</span>
              <span className="arrow">&rarr;</span>
            </Button>

            <Typography
              variant="body2"
              sx={{
                color: 'var(--ink-soft)',
                fontFamily: '"JetBrains Mono Variable", monospace',
                fontSize: '0.78rem',
              }}
            >
              {t('hero.gia_iscritto')}{' '}
              <Box
                component="a"
                href="https://app.yumaos.com/login"
                sx={{
                  color: 'var(--ink)',
                  textDecoration: 'underline',
                  textUnderlineOffset: '4px',
                  textDecorationColor: 'var(--sun)',
                  '&:hover': { color: 'var(--sun-deep)' },
                }}
              >
                {tCommon('accedi')}
              </Box>
            </Typography>
          </Stack>

          {/* Meta info — bottom of left col */}
          <Box
            sx={{
              display: 'flex',
              gap: { xs: 2.5, sm: 4 },
              rowGap: { xs: 2, sm: 4 },
              flexWrap: 'wrap',
              pt: 3,
              borderTop: '1px solid var(--dust)',
              opacity: 0,
              animation: 'fadeIn 700ms ease-out 2000ms forwards',
            }}
          >
            <Stack spacing={0.5}>
              <Typography
                variant="overline"
                sx={{ color: 'var(--ink-soft)', fontSize: '0.6rem' }}
              >
                {t('hero.meta_servizi_label')}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: 'var(--ink)', fontWeight: 500 }}
              >
                {t('hero.meta_servizi_value')}
              </Typography>
            </Stack>
            <Stack spacing={0.5}>
              <Typography
                variant="overline"
                sx={{ color: 'var(--ink-soft)', fontSize: '0.6rem' }}
              >
                {t('hero.meta_copertura_label')}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: 'var(--ink)', fontWeight: 500 }}
              >
                {t('hero.meta_copertura_value')}
              </Typography>
            </Stack>
            <Stack spacing={0.5}>
              <Typography
                variant="overline"
                sx={{ color: 'var(--ink-soft)', fontSize: '0.6rem' }}
              >
                {t('hero.meta_modello_label')}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: 'var(--ink)', fontWeight: 500 }}
              >
                {t('hero.meta_modello_value')}
              </Typography>
            </Stack>
          </Box>
        </Stack>

        {/* Right column: solar geometry */}
        <Box
          sx={{
            display: { xs: 'none', lg: 'flex' },
            justifyContent: 'center',
            alignItems: 'center',
            opacity: 0,
            animation: 'fadeIn 1200ms ease-out 600ms forwards',
          }}
        >
          <SolarGeometry size={460} markerText={t('hero.marker_solar')} />
        </Box>
      </Box>

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          padding: { xs: '24px 32px', md: '24px 64px' },
          borderTop: '1px solid var(--dust)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
          opacity: 0,
          animation: 'fadeIn 600ms ease-out 2300ms forwards',
        }}
      >
        <Typography
          variant="overline"
          sx={{ color: 'var(--ink-soft)', fontSize: '0.7rem' }}
        >
          {t('footer.tagline')}
        </Typography>
        <Typography
          variant="overline"
          sx={{ color: 'var(--ink-soft)', fontSize: '0.7rem' }}
        >
          {t('footer.ubicazione')}
        </Typography>
      </Box>
    </Box>
  );
}
